/**
 * Collections — lightweight named containers for thematic grouping.
 *
 * Collections are organizational metadata, not relationships.
 * They carry no governance or prioritization implications.
 *
 * INVARIANT: Collections must never be used as item pools, session inputs,
 * or ranking sets. Use promoteToItemPool() to explicitly create an Item Pool
 * from a Collection's membership — the Collection itself is never modified.
 *
 * SMART COLLECTIONS (migration 637, PRD Part 4.2): a collection with a
 * ruleQueryId is "smart" — its live membership is
 *   (rule matches, evaluated on read) UNION (pinned) MINUS (excluded)
 * Rule matches are NEVER stored as collection_memberships rows — only
 * 'manual' | 'pinned' | 'excluded' are persisted. See
 * src/lib/collections/smartCollectionMembership.ts for the resolver.
 */

export interface Collection {
  id: string;
  workspaceId?: string;
  ownerId?: string;
  name: string;
  description?: string;
  /** Saved query (saved_reports.id) whose PQL AST drives live membership. Undefined = static collection. */
  ruleQueryId?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface CollectionRow {
  id: string;
  workspace_id?: string | null;
  owner_id?: string | null;
  name: string;
  description?: string | null;
  rule_query_id?: string | null;
  created_at: string;
  updated_at: string;
}

/** Only explicit human overrides are ever persisted — rule matches are computed live, never stored. */
export type CollectionMembershipSource = 'manual' | 'pinned' | 'excluded';

export interface CollectionMembership {
  id: string;
  collectionId: string;
  itemId: string;
  workspaceId?: string;
  sortOrder: number;
  membershipSource: CollectionMembershipSource;
  addedBy?: string;
  addedAt: Date;
}

export interface CollectionMembershipRow {
  id: string;
  collection_id: string;
  item_id: string;
  workspace_id?: string | null;
  sort_order: number;
  membership_source?: string | null;
  added_by?: string | null;
  added_at: string;
}

/** Non-authoritative delta ledger row — powers "N new since you last looked" only. */
export interface CollectionSighting {
  collectionId: string;
  itemId: string;
  workspaceId: string;
  firstMatchedAt: Date;
}

export interface CollectionSightingRow {
  collection_id: string;
  item_id: string;
  workspace_id: string;
  first_matched_at: string;
}

/** Non-authoritative per-user "last looked at this collection" — delta badge baseline only. */
export interface CollectionView {
  collectionId: string;
  userId: string;
  lastViewedAt: Date;
}

export interface CollectionViewRow {
  collection_id: string;
  user_id: string;
  last_viewed_at: string;
}

export function collectionRowToDomain(row: CollectionRow): Collection {
  return {
    id: row.id,
    workspaceId: row.workspace_id ?? undefined,
    ownerId: row.owner_id ?? undefined,
    name: row.name,
    description: row.description ?? undefined,
    ruleQueryId: row.rule_query_id ?? undefined,
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
  };
}

export function membershipRowToDomain(row: CollectionMembershipRow): CollectionMembership {
  return {
    id: row.id,
    collectionId: row.collection_id,
    itemId: row.item_id,
    workspaceId: row.workspace_id ?? undefined,
    sortOrder: row.sort_order ?? 0,
    membershipSource: (row.membership_source as CollectionMembershipSource) || 'manual',
    addedBy: row.added_by ?? undefined,
    addedAt: new Date(row.added_at),
  };
}

export function sightingRowToDomain(row: CollectionSightingRow): CollectionSighting {
  return {
    collectionId: row.collection_id,
    itemId: row.item_id,
    workspaceId: row.workspace_id,
    firstMatchedAt: new Date(row.first_matched_at),
  };
}

export function viewRowToDomain(row: CollectionViewRow): CollectionView {
  return {
    collectionId: row.collection_id,
    userId: row.user_id,
    lastViewedAt: new Date(row.last_viewed_at),
  };
}
